import * as React from 'react';
import { useMemo, useState, useCallback } from 'react';
import { Loader2, Lock, Copy, ChevronRight, HelpCircle } from 'lucide-react';
import useOrgTemplates from '../../../application/routes/settings/components/TaskListManagement/hooks/useOrgTemplates';
import useOrgFindingTypes from '../../../application/routes/settings/components/TaskListManagement/hooks/useOrgFindingTypes';
import useOrgZones from '../../../application/routes/settings/components/TaskListManagement/hooks/useOrgZones';
import useTemplateMutations from '../../../application/routes/settings/components/TaskListManagement/hooks/useTemplateMutations';
import TemplateEditor from '../../../application/routes/settings/components/TaskListManagement/TemplateEditor';
import { PageHeader } from '../../components/page-header';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { useToast } from '../../components/ui/toast';
import { cn } from '../../lib/utils';

declare const global: {
  me: {
    id: string;
    orgID: string;
    imageName?: string;
    name: string;
  };
};

function getDisplayStatus(status: string): string {
  return status === 'published' ? 'Published' : 'Unpublished';
}

function getStatusBadgeClass(status: string): string {
  return status === 'published'
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
}

export function TaskListManagementPage() {
  const orgID = global?.me?.orgID;
  const isPaivcamSeed = orgID === 'paivcam-seed';

  const {
    templates,
    isLoading: templatesLoading,
    error: templatesError,
    refetch: refetchTemplates,
  } = useOrgTemplates();

  const {
    findingTypes,
    isLoading: findingTypesLoading,
    error: findingTypesError,
    refetch: refetchFindingTypes,
  } = useOrgFindingTypes();

  const {
    zones,
    isLoading: zonesLoading,
    error: zonesError,
    refetch: refetchZones,
  } = useOrgZones();

  const { cloneTemplate } = useTemplateMutations();

  const isLoading = templatesLoading || findingTypesLoading || zonesLoading;
  const error = templatesError || findingTypesError || zonesError;

  const systemTemplates = useMemo(
    () => templates.filter((t) => t.isSystem),
    [templates]
  );
  const orgTemplates = useMemo(
    () => templates.filter((t) => !t.isSystem),
    [templates]
  );

  const [viewingTemplateId, setViewingTemplateId] = useState<string | null>(
    null
  );
  const [cloning, setCloning] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const toast = useToast();

  const handleCloneSystemTemplate = useCallback(
    async (templateId: string) => {
      setCloning(true);
      try {
        const result = await cloneTemplate(templateId);
        if (result?.success) {
          toast.success('Template cloned successfully');
          refetchTemplates();
          refetchFindingTypes();
          refetchZones();
        } else {
          toast.error(result?.error ?? 'Failed to clone template');
        }
      } catch (err: any) {
        toast.error(err?.message ?? 'Failed to clone template');
      } finally {
        setCloning(false);
      }
    },
    [cloneTemplate, refetchTemplates, refetchFindingTypes, refetchZones, toast]
  );

  // Full-page loading on initial load
  if (isLoading && !viewingTemplateId && templates.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader
          title="Task List Management"
          avatar={{ src: global.me?.imageName, name: global.me?.name }}
        />
        <div className="flex items-center justify-center flex-1">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader
          title="Task List Management"
          avatar={{ src: global.me?.imageName, name: global.me?.name }}
        />
        <div className="p-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {error}
          </div>
        </div>
      </div>
    );
  }

  // Delegate to TemplateEditor when viewing a specific template
  if (viewingTemplateId) {
    return (
      <div className="flex flex-col h-full v2-task-list-management">
        <PageHeader
          title="Task List Management"
          avatar={{ src: global.me?.imageName, name: global.me?.name }}
        />
        <div className="flex-1 overflow-y-auto p-6">
          <TemplateEditor
            templates={templates}
            findingTypes={findingTypes}
            zones={zones}
            onRefreshTemplates={refetchTemplates}
            onRefreshFindingTypes={refetchFindingTypes}
            onRefreshZones={refetchZones}
            isPaivcamSeed={isPaivcamSeed}
            initialTemplateId={viewingTemplateId}
            onBackToList={() => setViewingTemplateId(null)}
          />
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Task List Management"
        avatar={{ src: global.me?.imageName, name: global.me?.name }}
      />

      <div className="flex-1 overflow-y-auto p-6 min-w-0">
        <div className="max-w-4xl mx-auto">
          {/* Header with help button */}
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-2xl font-bold text-foreground">
              Task List Management
            </h2>
            <button
              onClick={() => setHelpOpen(true)}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Help"
            >
              <HelpCircle className="size-5" />
            </button>
          </div>
          <p className="text-sm text-muted-foreground mb-6">
            Manage your organization&apos;s inspection templates, damage types,
            property features, and zones.
          </p>

          {/* Paiv Templates Section */}
          {systemTemplates.length > 0 && (
            <div className="mb-8">
              <h3 className="text-base font-semibold text-foreground mb-3">
                Paiv Templates
              </h3>
              <div className="flex flex-col gap-3">
                {systemTemplates.map((t) => {
                  const itemCount = (t.items ?? []).filter(
                    (i) => !i.isDeleted
                  ).length;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setViewingTemplateId(t.id)}
                      className={cn(
                        'flex items-center w-full px-4 py-3 rounded-xl border border-border bg-card text-left',
                        'hover:border-primary hover:bg-muted/50 transition-colors cursor-pointer'
                      )}
                    >
                      <Lock className="size-4 text-muted-foreground mr-3 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">
                          {t.name}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {itemCount} tasks &middot; v{t.version}
                          {t.description ? ` \u00B7 ${t.description}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-3">
                        <Badge
                          className={cn(
                            'border-0 text-xs font-medium',
                            getStatusBadgeClass(t.status)
                          )}
                        >
                          {getDisplayStatus(t.status)}
                        </Badge>
                        {!isPaivcamSeed && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={cloning}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCloneSystemTemplate(t.id);
                            }}
                            className="text-xs"
                          >
                            <Copy className="size-3.5" />
                            Clone
                          </Button>
                        )}
                        <ChevronRight className="size-4 text-muted-foreground" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Your Templates Section */}
          <div>
            <h3 className="text-base font-semibold text-foreground mb-3">
              Your Templates
            </h3>
            {orgTemplates.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No custom templates yet. Clone a Paiv template above to get
                started.
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                {orgTemplates.map((t) => {
                  const itemCount = (t.items ?? []).filter(
                    (i) => !i.isDeleted
                  ).length;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setViewingTemplateId(t.id)}
                      className={cn(
                        'flex items-center w-full px-4 py-3 rounded-xl border border-border bg-card text-left',
                        'hover:border-primary hover:bg-muted/50 transition-colors cursor-pointer'
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">
                          {t.name}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {itemCount} tasks &middot; v{t.version}
                          {t.description ? ` \u00B7 ${t.description}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-3">
                        <Badge
                          className={cn(
                            'border-0 text-xs font-medium',
                            getStatusBadgeClass(t.status)
                          )}
                        >
                          {getDisplayStatus(t.status)}
                        </Badge>
                        <ChevronRight className="size-4 text-muted-foreground" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Help Dialog */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>How Task List Management Works</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 text-sm">
            <div>
              <h4 className="font-semibold text-foreground mb-1">Templates</h4>
              <p className="text-muted-foreground">
                A template defines the full inspection workflow — the checklist
                tasks, damage types, property features, and zones that
                inspectors use. Multiple templates can be published at once —
                inspectors choose which template to use when starting an
                inspection. Only one version of each template may be published
                at a time; publishing a new version automatically unpublishes
                the previous one. Clone a Paiv template to get started, then
                customize it for your team.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-foreground mb-1">Checklist</h4>
              <p className="text-muted-foreground">
                The checklist is the ordered list of tasks every inspector
                completes — property photos, material close-ups, condition
                notes, etc. Tasks are grouped by zone and completed in order
                during an inspection.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-foreground mb-1">
                Damage Types
              </h4>
              <p className="text-muted-foreground">
                Damage types are what Hank matches when an inspector verbally
                reports damage. Use clear, natural names like &quot;Hail
                Damage&quot; or &quot;Missing Shingles&quot; — Hank uses the
                name and description to match spoken words. Add a detailed
                description with keywords inspectors might say to improve
                matching accuracy.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-foreground mb-1">
                Property Features
              </h4>
              <p className="text-muted-foreground">
                Property features are structural elements or characteristics
                that may or may not be present on a property — like chimneys,
                skylights, or pipe jacks. When an inspector mentions a feature,
                Hank matches it by name and description. Each feature can have
                required documentation tasks (photos, questions).
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-foreground mb-1">Zones</h4>
              <p className="text-muted-foreground">
                Zones organize tasks by location — like &quot;Ground Level&quot;
                and &quot;Roof Level&quot;. Inspectors work through tasks zone
                by zone. You can create custom zones to match your inspection
                workflow.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-foreground mb-1">
                Naming Tips for Hank
              </h4>
              <p className="text-muted-foreground">
                Hank uses trigram similarity to match what inspectors say to
                your damage types and property features. For best results: use
                plain English names (not codes), add descriptions with common
                synonyms and keywords, and test by saying the name out loud — if
                it sounds natural, Hank will match it.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setHelpOpen(false)}>Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
